import { Loader } from "apps/visits/packages/ui/src/components/loader";
import { useRef } from "react";
import { matchPath } from "react-router-dom";
import { IntersectionFadeIn } from "@/core/shared/v2/ui/intersection-fade-in";
import { useGetOrder } from "../../api/get-order";
import { ThankYou } from "../../components/thank-you";
import type { Order } from "../../models/order";
import { getConfig } from "../../models/voucher-distribution";
import { useVouchersDistributionState } from "../../store/use-vouchers-distribution-state";
import { purchaseEvent, safeTrackEvent } from "../../tracking/events";
import { clearGaUserType, readGaUserType } from "../../utils/ga-user-type";
import { consumeVdItemListIds } from "../../utils/vd-item-list-ids";
import { configName } from "../config/config-name";
import { routes } from "../config/routes";

export const ThankYouPage = () => {
  const { voucherDistribution } = useVouchersDistributionState();
  const config = getConfig(voucherDistribution, configName);
  const fullPath = config ? config.path + routes.thankYou.raw() : "";
  const trackedTransactionIdRef = useRef<string>();

  const matchPathThankYou = matchPath<{ uuid: Order["uuid"] }>(window.location.pathname, {
    path: fullPath,
    exact: false,
    strict: false,
  });

  const { data: orderData, isLoading: isOrderLoading } = useGetOrder({
    slug: voucherDistribution?.slug || "",
    configName,
    uuid: matchPathThankYou?.params.uuid || "",
    config: {
      enabled: !!matchPathThankYou?.params.uuid && !!voucherDistribution,
      onSuccess: (orderData) => {
        if (trackedTransactionIdRef.current === orderData.order.uuid) return;
        trackedTransactionIdRef.current = orderData.order.uuid;

        const orderFor = sessionStorage.getItem("vd_order_for") || undefined;
        sessionStorage.removeItem("vd_order_for");

        const itemListIds = consumeVdItemListIds();
        const userType = readGaUserType();
        const totalValue = Number(orderData.order.totalGrossPrice);

        safeTrackEvent(
          () =>
            purchaseEvent(
              orderData?.order?.items.map((item) => ({
                ...item,
                productType: "blood-test-package",
                uuid: item.productUuid,
                quantity: 1,
                ...(itemListIds[item.productUuid] && { item_list_id: itemListIds[item.productUuid] }),
              })),
              {
                value: totalValue,
                transactionId: orderData.order.uuid,
              },
              {
                paymentType: totalValue === 0 ? "free" : "PayU",
                orderFor,
                numberOfProducts: orderData.order.items.length,
                userType,
              },
            ),
          "purchase",
          "friends-and-family thank-you",
        );

        // Mirrors GAO TYP — clear once the purchase event has fired so a fresh browsing
        // session starts without a stale userType bleeding into unrelated journeys.
        clearGaUserType();
      },
    },
  });

  if (!voucherDistribution || isOrderLoading || !orderData)
    return (
      <div className="fixed inset-0 z-50 flex h-screen w-full items-center justify-center">
        <Loader />
      </div>
    );

  return (
    <IntersectionFadeIn className="mx-auto max-w-[1200px] py-10">
      <ThankYou
        vouchers={orderData.order.items}
        voucherDistribution={voucherDistribution}
        configName={configName}
        totalGrossPrice={orderData.order.totalGrossPrice}
        showCurrentConfig
      />
    </IntersectionFadeIn>
  );
};
